import {DefaultPageProps} from '@models/DefaultProps.ts';
import type {PermissaoAplicacao} from "@models/seguranca/permissaoApp.ts";
import {capitalize, Grid, LinearProgress, Paper, SxProps, Typography} from "@mui/material";
import {DefaultPagination} from '@shared/specialComponents/DefaultPagination.tsx';
import React, {useMemo} from "react";
import {useAuth} from "@contexts/hooks/UseAuth.tsx";

/**
 * @param `UpperComponent` Representa o que deve ser renderizado antes da primeira paginação
 * @param `DownComponent` Representa o que deve ser renderizado após a ultima paginação
 * @param `titulo` Titulo da pagina
 * @param `children` Conteudo da pagina
 * @param `size` Tamanho da pagina
 * @param `component` Componente que será renderizado
 * @param `spacing` Espaçamento entre os componentes
 * @param `permissaoNecessaria` Permissão necessária para acessar a pagina
 * @param `sx` Sx do componente principal
 * @param `pageProps` Props da paginação
 * @param `hideHeader` Se deve ou não mostrar o header da pagina
 */
interface DefaultContainerProps extends DefaultPageProps {
    titulo?: string;
    children?: React.ReactNode;
    size?: 'small' | 'medium' | 'large' | 'max';
    component?: any;
    spacing?: number;
    permissaoNecessaria?: PermissaoAplicacao | PermissaoAplicacao[];
    sx?: SxProps;
    innerSx?: SxProps;
    pageProps?: Omit<React.ComponentProps<typeof DefaultPagination>, 'posicao'>;
    UpperComponent?: React.ReactNode;
    BottomComponent?: React.ReactNode;
}

export function DefaultContainer(props: DefaultContainerProps) {
    const {
        titulo,
        children,
        size,
        component,
        spacing,
        permissaoNecessaria,
        innerSx,
    } = props;
    const {hasAccess, permissoes} = useAuth();

    const permissionDenied = useMemo(() => {
        return permissaoNecessaria && !hasAccess(permissaoNecessaria);
    }, [permissoes])
    const isLoading = props.pageProps?.pagination?.loading;

    return (
        <Grid container
              sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', ...props.sx}}
              spacing={spacing || 0}
              alignItems={'center'}
              justifyContent={'center'}
              maxWidth={'100%'}
              width={'100%'}>
            <Grid width={'100%'}
                  maxWidth={{'small': '360px', 'medium': '660px', 'large': '1260px', 'max': '100%'}[size || 'large']}
                  component={(component ?? Paper)}
                  p={titulo && 2}
                  px={props.hideHeader == true ? 0 : 1}
            >
                {permissionDenied && <Grid container
                                           justifyContent={'center'}
                                           component={Paper} p={3}>
                    <Grid size={12}>
                        <Grid container justifyContent={'center'}>
                            <Typography variant={'h5'}>ACESSO NEGADO!</Typography>
                        </Grid>
                    </Grid>
                    <Grid size={12}>
                        <Grid container justifyContent={'center'}>
                            <Typography variant={'body2'} color={'text.secondary'}>
                                Você não tem permissão para acessar essa página
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid> || <Grid container>
                    {!props.hideHeader && <Grid size={12} pb={1}>
                        <Typography variant={'h5'}
                                    textAlign={'center'}>
                            {titulo && capitalize(titulo)}
                        </Typography>
                    </Grid>}
                    {props.UpperComponent && <Grid size={12} mb={1}>
                        {props.UpperComponent}
                    </Grid>}
                    <Grid size={12}>
                        {props.pageProps && <DefaultPagination {...props.pageProps} posicao={'top'}/>}
                    </Grid>
                    <Grid size={12}>
                        {props.pageProps?.pagination && <Grid size={12}>
                            <LinearProgress color={'primary'} value={isLoading ? 100 : 0}
                                            variant={isLoading ? 'query' : 'determinate'}/>
                        </Grid>}
                        <Grid size={12} sx={{...innerSx}}>
                            {children}
                        </Grid>
                    </Grid>
                    <Grid size={12}>
                        {props.pageProps && <DefaultPagination {...props.pageProps} posicao={'bottom'}/>}
                    </Grid>

                    {props.BottomComponent && <Grid size={12} mt={1}>
                        {props.BottomComponent}
                    </Grid>}
                </Grid>}
            </Grid>
        </Grid>)
}